import { Link, useNavigate } from "@tanstack/react-router";
import {
  Clock,
  GitBranch,
  LayoutGrid,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Search,
  Table2,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { CopyPromptButton } from "@/components/solutions/CopyPromptButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useLocalStorageBoolean } from "@/hooks/use-local-storage";
import {
  type SolutionListRow,
  useCreateSolution,
  useDeleteSolution,
  useSolutions,
} from "@/hooks/use-solutions";
import { useSession } from "@/lib/auth-client";

function canDeleteSolution(
  s: SolutionListRow,
  user: { id: string; role?: string | null } | undefined,
): boolean {
  if (!user) return false;
  return user.role === "admin" || s.createdBy === user.id;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function SolutionCard({ s }: { s: SolutionListRow }) {
  return (
    <Link
      to="/solutions/$slug"
      params={{ slug: s.slug }}
      className="block transition-transform hover:-translate-y-0.5"
    >
      <Card className="h-full border-border/70 hover:border-primary/40 hover:shadow-sm transition">
        <CardContent className="p-5 space-y-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg leading-tight">{s.name}</h3>
              {s.currentVersionNumber !== null && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                  v{s.currentVersionNumber}
                </span>
              )}
            </div>
            {s.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{s.description}</p>
            )}
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 border-t border-border/40">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatRelative(s.updatedAt)}
            </span>
            <span className="flex items-center gap-1">
              <GitBranch className="h-3 w-3" />
              {s.versionCount} {s.versionCount === 1 ? "version" : "versions"}
            </span>
            {s.unresolvedCommentCount > 0 && (
              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                <MessageSquare className="h-3 w-3" />
                {s.unresolvedCommentCount} open
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function SolutionRow({
  s,
  canDelete,
  onDelete,
}: {
  s: SolutionListRow;
  canDelete: boolean;
  onDelete: (s: SolutionListRow) => void;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={() => navigate({ to: "/solutions/$slug", params: { slug: s.slug } })}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium">{s.name}</span>
          {s.currentVersionNumber !== null && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              v{s.currentVersionNumber}
            </span>
          )}
        </div>
        {s.description && (
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{s.description}</p>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground tabular-nums">{s.versionCount}</td>
      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
        {new Date(s.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 tabular-nums">
        {s.unresolvedCommentCount > 0 ? (
          <span className="text-amber-700 dark:text-amber-400">{s.unresolvedCommentCount}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
                aria-label={`Actions for ${s.name}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDelete(s)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
}

function CreateSolutionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useCreateSolution();

  const submit = async () => {
    if (!name.trim()) return;
    const created = await mutation.mutateAsync({
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setName("");
    setDescription("");
    onOpenChange(false);
    navigate({ to: "/solutions/$slug", params: { slug: created.slug } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New solution</DialogTitle>
          <DialogDescription>
            Give the solution a name. You can upload an architecture file on the next screen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="sol-name">Name</Label>
            <Input
              id="sol-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Wolf DMS"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sol-desc">Description (optional)</Label>
            <Textarea
              id="sol-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One-line summary of what this solution does"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || mutation.isPending}>
            Create solution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SolutionsDashboardPage() {
  const { data, isLoading, isError, refetch } = useSolutions();
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [tableView, setTableView] = useLocalStorageBoolean("solutions:view:table", false);
  const [deleteTarget, setDeleteTarget] = useState<SolutionListRow | null>(null);
  const deleteMutation = useDeleteSolution();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        s.slug.toLowerCase().includes(term) ||
        (s.description ?? "").toLowerCase().includes(term),
    );
  }, [data, search]);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-6">
        <div className="space-y-1 flex-1">
          <h2 className="text-3xl font-bold tracking-tight">Solutions</h2>
          <p className="text-sm text-muted-foreground">
            Living architectures for every Nymbl solution.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search solutions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-56"
            />
          </div>
          <div className="flex items-center rounded-md border border-border p-0.5">
            <Button
              variant={tableView ? "ghost" : "secondary"}
              size="icon"
              className="h-7 w-7"
              onClick={() => setTableView(false)}
              aria-label="Card view"
              aria-pressed={!tableView}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={tableView ? "secondary" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => setTableView(true)}
              aria-label="Table view"
              aria-pressed={tableView}
            >
              <Table2 className="h-4 w-4" />
            </Button>
          </div>
          <CopyPromptButton />
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New solution
          </Button>
        </div>
      </div>

      {isLoading && !tableView && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive mb-3">Could not load solutions.</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (data?.length ?? 0) === 0 && (
        <div className="rounded-lg border-2 border-dashed border-border bg-muted/30 p-12 text-center">
          <h3 className="text-lg mb-1">No solutions yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first solution to start visualizing architecture.
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New solution
          </Button>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (data?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-border bg-muted/20 p-8 text-center">
          <h3 className="text-base mb-1">No matches for "{search}"</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Try a different name, slug, or description fragment.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch("")}>
            Clear search
          </Button>
        </div>
      )}

      {!tableView && !isLoading && filtered.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <SolutionCard key={s.id} s={s} />
          ))}
        </div>
      )}

      {tableView && !isError && (isLoading || filtered.length > 0) && (
        <DataTable
          loading={isLoading}
          headers={[
            { label: "Solution" },
            { label: "Versions" },
            { label: "Created On", className: "whitespace-nowrap" },
            { label: "Open Questions" },
            { label: "", className: "w-12" },
          ]}
        >
          {filtered.map((s) => (
            <SolutionRow
              key={s.id}
              s={s}
              canDelete={canDeleteSolution(s, session?.user)}
              onDelete={setDeleteTarget}
            />
          ))}
        </DataTable>
      )}

      <div className="mt-10 text-xs text-muted-foreground border-t border-border/40 pt-4">
        Need a quick preview without saving?{" "}
        <Link to="/archviz" className="underline hover:text-foreground">
          Drop an architecture file
        </Link>{" "}
        on the in-memory canvas.
      </div>

      <CreateSolutionDialog open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={`Delete "${deleteTarget?.name}"?`}
        description="This permanently deletes the solution and all of its versions, comments, and share links. This cannot be undone."
        confirmLabel="Delete solution"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!deleteTarget) return;
          await deleteMutation.mutateAsync(deleteTarget.slug);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
